"use client";

import React, { useEffect, useMemo, useState } from "react";
import { CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { QRCodeSVG } from "qrcode.react";
import logo from "../assets/images/logo.svg";
import { Copy, Check } from "lucide-react";
import AssetNetSelector from "./AssetNetworkSelector";
import qrPlaceHolder from "../assets/images/qrCode.png";
import { useToast } from "../hooks/use-toast";
import info from "../assets/images/system.png";
import { Skeleton } from "./ui/skeleton";
import { buildUniversalWalletPaymentUri } from "../utils/walletPaymentUri";

const formatNetworkLabel = (chain, asset) => {
  if (!chain) {
    return "";
  }
  const chainLabel = String(chain);
  const assetSymbol = asset?.asset?.toUpperCase();
  if (assetSymbol && chainLabel === `${assetSymbol}_NETWORK`) {
    return `${asset?.assetName || assetSymbol} Network`;
  }
  return chainLabel.replace(/_/g, " ");
};

const Receive = ({ hotActionAsset, tradingAssets, tradingNets }) => {
  const [selectedWallet, setSelectedWallet] = useState([]);
  const [selectedNet, setSelectedNet] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingTradeAssets, setLoadingTradeAssets] = useState(true);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleCopy = () => {
    const textToCopy =
      selectedWallet?.asset === "btc"
        ? selectedWallet?.walletIdLegacy
        : selectedWallet?.walletId;

    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        description: "Wallet Address Copied Successfully",
        variant: "eighty",
      });
    }
  };

  const handleTagCopy = () => {
    if (selectedWallet?.tag) {
      navigator.clipboard.writeText(selectedWallet?.tag);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        description: "Tag Number Copied Successfully",
        variant: "eighty",
      });
    }
  };

  useEffect(() => {
    if (!tradingAssets || !tradingNets) {
      setLoadingTradeAssets(true);
    } else {
      setLoadingTradeAssets(false);
    }
  }, [tradingAssets, tradingNets]);

  const depositAddress = useMemo(() => {
    if (!selectedWallet || selectedWallet.length === 0) {
      return "";
    }
    return selectedWallet?.asset === "btc"
      ? selectedWallet?.walletIdLegacy
      : selectedWallet?.walletId;
  }, [selectedWallet]);

  const qrEncodedValue = useMemo(() => {
    if (!depositAddress) {
      return "";
    }
    const universal = buildUniversalWalletPaymentUri({
      address: depositAddress,
      assetSymbol: selectedWallet?.asset,
      chainLabel: selectedNet?.chain,
      chain: selectedNet,
    });
    return universal || depositAddress;
  }, [depositAddress, selectedWallet?.asset, selectedNet]);

  const handleWallet = (wallet, noWallet, chain) => {
    setLoading(true);

    if (chain?.length !== 0) {
      setSelectedNet(chain);
      setLoading(false);
    }

    if (wallet?.length !== 0) {
      setSelectedWallet(wallet);
      setLoading(false);
    }

    if (noWallet === 1) {
      setSelectedWallet([]);
      toast({
        description: "Wallet Not Found",
        variant: "eighty",
      });
    }
  };

  return (
    <CardContent className="md:px-6 px-2">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 lg:gap-6">
        {/* Left Column - Content */}
        <div className="space-y-4 lg:space-y-2 lg:col-span-3">
          {loadingTradeAssets ? (
            <div className="w-full flex items-center justify-between py-4 px-4 rounded-2xl border border-gray-300 bg-muted/50">
              <div className="flex items-center gap-3">
                <Skeleton className="w-10 h-10 rounded-full bg-gray-300" />
                <div className="flex flex-col gap-1">
                  <Skeleton className="w-28 h-5 rounded-full bg-gray-300" />
                </div>
              </div>
              <Skeleton className="w-6 h-6 rounded-full bg-gray-300" />
            </div>
          ) : (
            <AssetNetSelector
              hotActionAsset={hotActionAsset}
              onAssetSelect={setSelectedAsset}
              onWalletSelect={handleWallet}
              tradingAssets={tradingAssets}
              tradingNets={tradingNets}
              loadingAsset={loading}
              setLoadingAsset={setLoading}
            />
          )}

          {loading ? (
            <Skeleton className="flex items-center gap-7 border rounded-2xl p-4 lg:p-8 bg-gray-400 w-full" />
          ) : selectedWallet?.tag ? (
            <div className="space-y-4 lg:space-y-2">
              <div className="space-y-2">
                <label className="text-gray-600 text-sm">Wallet Address</label>
                <div className="flex items-center gap-3 lg:gap-4 border rounded-2xl p-3 lg:p-4 text-gray-400">
                  <div className="text-sm break-all whitespace-pre-wrap flex-1">
                    {selectedWallet?.asset === "btc"
                      ? selectedWallet?.walletIdLegacy
                      : selectedWallet?.walletId}
                  </div>
                  <Button
                    variant="outline"
                    className="flex-shrink-0 rounded-full w-8 h-8 lg:w-10 lg:h-10 relative"
                    onClick={handleCopy}
                  >
                    {copied ? (
                      <Check className="h-4 w-4 lg:h-5 lg:w-5 text-green-500 absolute" />
                    ) : (
                      <Copy className="h-4 w-4 lg:h-5 lg:w-5 text-gray-500 absolute" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-gray-600 text-sm">Tag Number</label>
                <div className="flex items-center justify-between gap-3 lg:gap-4 border rounded-2xl p-3 lg:p-4 text-gray-400">
                  <div className="text-sm">{selectedWallet?.tag}</div>
                  <Button
                    variant="outline"
                    className="flex-shrink-0 rounded-full w-8 h-8 lg:w-10 lg:h-10 relative"
                    onClick={handleTagCopy}
                  >
                    {copied ? (
                      <Check className="h-4 w-4 lg:h-5 lg:w-5 text-green-500 absolute" />
                    ) : (
                      <Copy className="h-4 w-4 lg:h-5 lg:w-5 text-gray-500 absolute" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            selectedWallet?.length !== 0 && (
              <div className="space-y-2">
                <label className="text-gray-600 text-sm">Wallet Address</label>
                <div className="flex items-center justify-between gap-3 lg:gap-4 border rounded-2xl p-3 lg:p-4 text-gray-400">
                  <div className="text-sm break-all whitespace-pre-wrap flex-1">
                    {selectedWallet?.asset === "btc"
                      ? selectedWallet?.walletIdLegacy
                      : selectedWallet?.walletId}
                  </div>
                  <Button
                    variant="outline"
                    className="flex-shrink-0 rounded-full w-8 h-8 lg:w-10 lg:h-10 relative"
                    onClick={handleCopy}
                  >
                    {copied ? (
                      <Check className="h-4 w-4 lg:h-5 lg:w-5 text-green-500 absolute" />
                    ) : (
                      <Copy className="h-4 w-4 lg:h-5 lg:w-5 text-gray-500 absolute" />
                    )}
                  </Button>
                </div>
              </div>
            )
          )}
        </div>

        {/* Right Column - QR Code */}
        {loading ? (
          <Skeleton className="space-y-2 lg:col-span-2 bg-gray-400 w-full h-[200px] lg:h-auto" />
        ) : selectedWallet?.length === 0 ? (
          <div className="space-y-2 lg:col-span-2 flex justify-center lg:justify-start">
            <img
              src={qrPlaceHolder}
              className="w-full h-auto"
              alt="QR Placeholder"
            />
          </div>
        ) : !depositAddress ? (
          <div className="space-y-2 lg:col-span-2 flex flex-col items-center justify-center min-h-[200px] rounded-2xl border border-red-200 bg-red-50 px-4 text-center text-sm text-red-800">
            No deposit address is available for this asset and network.
          </div>
        ) : (
          <div className="space-y-2 lg:col-span-2 flex justify-center lg:justify-start w-full">
            <div className="border p-4 lg:p-[20px] rounded-2xl inline-block max-w-full">
              <QRCodeSVG
                value={qrEncodedValue}
                size={200}
                level="H"
                imageSettings={{
                  src: logo,
                  height: 40,
                  width: 40,
                  excavate: true,
                }}
              />
            </div>
          </div>
        )}
      </div>

      {selectedWallet?.length !== 0 && (
        <div className="flex items-center justify-center mt-5 text-center gap-3 lg:gap-4 bg-orange-50 font-semibold rounded-2xl p-3 lg:p-4 text-orange-800 text-sm lg:text-base">
          <img
            src={info}
            alt="info"
            width={16}
            height={16}
            className="flex-shrink-0"
          />
          {selectedAsset?.asset && selectedNet?.chain
            ? `Only send ${selectedAsset.asset.toUpperCase()} on ${formatNetworkLabel(
                selectedNet.chain,
                selectedAsset
              )} to this wallet address. Other assets or networks may be lost.`
            : selectedNet?.depositWarning}
        </div>
      )}
    </CardContent>
  );
};

export default Receive;
